import React from 'react';

import PropTypes from 'prop-types';
import { HiDotsVertical } from 'react-icons/hi';
import { BiSliderAlt } from 'react-icons/bi';
import { IoSearch } from 'react-icons/io5';

import {
  Indicator,
  Menu,
  Button,
  FormInput,
  FilterBar,
  showDrawer,
  Text,
  DropdownButton,
} from 'modules/common';
import { removeEmpty, removeNull } from 'modules/common/utils';

const SearchElement = (props) => (
  <FormInput
    placeholder="Search"
    name="keyword"
    label={false}
    className="w-full"
    leadingItem={<IoSearch className="h-4 w-4" />}
    {...props}
  />
);

const RenderFilter = ({ onClose, content, value, withSearch, onChange }) => {
  const [form, setForm] = React.useState(value);

  const dispatchContent = () => {
    if (typeof content === 'function') {
      return content({
        state: form,
        setState: setForm,
        onClose,
      });
    }

    return content;
  };

  React.useEffect(() => {
    setForm(value);
  }, [value]);

  return (
    <div className="flex flex-col flex-1 gap-ds-12">
      <div className="flex flex-col flex-1 gap-ds-12">
        {withSearch && (
          <SearchElement
            wrapperClassName="block sm:hidden"
            value={form?.keyword}
            onChange={setForm}
          />
        )}

        {dispatchContent()}
      </div>

      <div className="flex items-center gap-ds-8">
        <Button
          label="Clear"
          onClick={() => {
            setForm({});
          }}
        />
        <Button
          label="Apply"
          type="primary"
          className="flex-1"
          onClick={() => {
            setForm(form);
            onChange(form);
            onClose();
          }}
        />
      </div>
    </div>
  );
};

RenderFilter.defaultProps = {
  content: false,
  value: {},
  withSearch: false,
  onClose: () => {},
  onChange: () => {},
};

RenderFilter.propTypes = {
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  onClose: PropTypes.instanceOf(Function),
  value: PropTypes.instanceOf(Object),
  onChange: PropTypes.instanceOf(Function),
  withSearch: PropTypes.bool,
};

export const onFilterChange = (value, { setQuery } = {}) => {
  const args = {
    ...value,
    selected: '',
    page: 1,
  };

  setQuery(removeEmpty(args));
};

export const onPageChange = (page, { query, setQuery } = {}) => {
  const args = {
    ...query,
    selected: '',
    page,
  };

  setQuery(removeEmpty(args));
};

function ActionBar({
  withSearch,
  withFilter,
  withFilterBar,
  withMenu,
  withPrimaryButton,
  primaryButtonProps,
  drawerContent,
  value: $value,
  onChange,
  menuProps,
  withDropdownButton,
  dropdownButtonProps,
}) {
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef();
  const [form, setForm] = React.useState(removeEmpty(removeNull($value)));
  const [keyword, setKeyword] = React.useState({
    keyword: form?.keyword,
  });

  const filterLen = Object.keys(
    Object.entries(form)
      ?.filter((item) => !['per_page', 'selected', 'page']?.includes(item?.[0]))
      ?.filter((item) => item?.[1])
  )?.length;

  const handleOnFilter = () => {
    showDrawer({
      title: 'Filters',
      subTitle: 'Sort the data of your preferences',
      content: (onClose) => (
        <RenderFilter
          value={form}
          content={drawerContent}
          onClose={onClose}
          withSearch={withSearch}
          onChange={onChange}
        />
      ),
    });
  };

  const handleOnMenu = () => setShowMenu(!showMenu);

  React.useEffect(() => {
    setForm(removeEmpty(removeNull($value)));
    setKeyword({
      keyword: $value?.keyword,
    });
  }, [$value]);

  const timeout = React.useRef();

  return (
    <div className="flex flex-col gap-ds-8">
      <div className="flex items-center gap-ds-8">
        <div className="flex items-center gap-ds-8 flex-1">
          {withSearch && (
            <div className="flex-1 max-w-[250px] hidden sm:block">
              <SearchElement
                onChange={(v) => {
                  clearTimeout(timeout.current);

                  setKeyword(() => {
                    const $v = v();

                    timeout.current = setTimeout(() => {
                      setForm((prevState) => {
                        const $v2 = { ...prevState, ...$v };

                        onChange($v2);

                        return $v2;
                      });
                    }, 1500);

                    return $v;
                  });
                }}
                value={keyword?.keyword || ''}
              />
            </div>
          )}

          {withFilter && (
            <div className="relative">
              <Button
                label={
                  <>
                    <BiSliderAlt className="h-4 w-4 block sm:hidden" />
                    <span className="hidden sm:block">Filters</span>
                  </>
                }
                className="!px-ds-12 sm:!px-ds-16"
                onClick={handleOnFilter}
              />

              {filterLen > 0 && (
                <Indicator
                  size="lg"
                  type="danger"
                  positionClassName="-top-1 -right-1"
                  isAbsolute
                >
                  <Text
                    color="primitive-white"
                    className="text-ds-10 font-medium"
                  >
                    {filterLen}
                  </Text>
                </Indicator>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-ds-8">
          {withDropdownButton && <DropdownButton {...dropdownButtonProps} />}

          {withPrimaryButton && (
            <Button
              label="Primary Action"
              type="primary"
              {...primaryButtonProps}
            />
          )}

          {withMenu && (
            <div className="relative" ref={menuRef}>
              <Button
                label={<HiDotsVertical className="h-4 w-4" />}
                className="p-ds-12"
                onClick={handleOnMenu}
              />

              {showMenu && (
                <Menu
                  width="150px"
                  className="-right-0.5 mt-1"
                  clickOutsideProps={{
                    ref: menuRef,
                    set: setShowMenu,
                  }}
                  {...menuProps}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {withFilterBar && (
        <FilterBar
          value={form}
          onChange={(v) => {
            setKeyword({ keyword: v?.keyword });
            setForm(v);
            onChange(v);
          }}
        />
      )}
    </div>
  );
}

ActionBar.defaultProps = {
  primaryButtonProps: {},
  withSearch: true,
  withFilter: true,
  withFilterBar: true,
  withMenu: true,
  withPrimaryButton: true,
  withDropdownButton: false,
  drawerContent: '',
  menuProps: {},
  filterBarProps: {},
  dropdownButtonProps: {},
  value: {},
  onChange: () => {},
};

ActionBar.propTypes = {
  primaryButtonProps: PropTypes.instanceOf(Object),
  menuProps: PropTypes.instanceOf(Object),
  dropdownButtonProps: PropTypes.instanceOf(Object),
  filterBarProps: PropTypes.instanceOf(Object),
  drawerContent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  withSearch: PropTypes.bool,
  withFilter: PropTypes.bool,
  withDropdownButton: PropTypes.bool,
  value: PropTypes.instanceOf(Object),
  onChange: PropTypes.instanceOf(Function),
  withFilterBar: PropTypes.bool,
  withMenu: PropTypes.bool,
  withPrimaryButton: PropTypes.bool,
};

export default ActionBar;
